<template>
    <div class="boxlogo">
        <!-- 品牌 logo（与登录/注册页一致） -->
        <div class="brand">
            <img class="logo" :src="require('@/assets/images/img/logo_dt.png')" />
            <span class="brand-divider"></span>
            <div class="brand-text">
                <h1 class="brand-title">FreshMon Pulse</h1>
                <p class="brand-subtitle">ALWAYS FRESH · ALWAYS IN CONTROL</p>
            </div>
        </div>

        <!-- 八边形卡片 -->
        <div class="login_in_wrap">
            <section class="login_in">
                <!-- 状态：发送中 -->
                <template v-if="status === 'sending'">
                    <h2 class="loginTitle">{{ $t('account_redesign.up_email_sending') }}</h2>
                    <div class="verify-tip">
                        <i class="el-icon-loading"></i>
                        <span>{{ $t('account_redesign.up_email_sending_tip', { email: pendingEmail || '—' }) }}</span>
                    </div>
                </template>

                <!-- 状态：发送成功，显示验证码输入 -->
                <template v-else-if="status === 'verify'">
                    <h2 class="loginTitle">{{ $t('account_redesign.up_email_verify_title') }}</h2>
                    <div class="verify-tip">
                        <i class="el-icon-message"></i>
                        <span>{{ $t('account_redesign.up_email_verify_tip', { email: pendingEmail || '—' }) }}</span>
                    </div>
                    <el-form
                        autocomplete="on"
                        :model="form"
                        :rules="rules"
                        ref="form"
                        class="login_form"
                        label-position="left"
                        @submit.native.prevent
                    >
                        <el-form-item prop="code">
                            <el-input
                                name="code"
                                type="text"
                                v-model.trim="form.code"
                                autocomplete="off"
                                :placeholder="$t('account_redesign.up_email_verify_code')"
                                maxlength="10"
                                @keyup.native.enter="handleSubmit"
                            >
                                <i slot="prefix" class="icon_prefix el-icon-key"></i>
                            </el-input>
                        </el-form-item>
                        <el-form-item>
                            <el-button :loading="loading" @click="handleSubmit">
                                {{ $t('account_redesign.up_email_verify_title') }}
                            </el-button>
                        </el-form-item>
                        <el-form-item class="resend-item">
                            <el-button
                                type="text"
                                :disabled="resendCooldown > 0 || resending"
                                @click="handleResend"
                            >
                                <span v-if="resendCooldown > 0">{{ $t('account_redesign.up_email_resend_wait', { s: resendCooldown }) }}</span>
                                <span v-else>{{ $t('account_redesign.up_email_resend') }}</span>
                            </el-button>
                        </el-form-item>
                    </el-form>
                </template>

                <!-- 状态：发送失败 -->
                <template v-else-if="status === 'error'">
                    <h2 class="loginTitle">{{ $t('account_redesign.up_email_send_failed') }}</h2>
                    <div class="verify-tip error-tip">
                        <i class="el-icon-warning"></i>
                        <span>{{ errorMsg || $t('account_redesign.up_email_send_failed_tip') }}</span>
                    </div>
                    <el-button class="back-btn" type="primary" @click="goToProfile">
                        {{ $t('account_redesign.up_email_back_to_profile') }}
                    </el-button>
                </template>

                <!-- 状态：无待处理邮箱（直接访问此页面） -->
                <template v-else>
                    <h2 class="loginTitle">{{ $t('account_redesign.up_email_verify_title') }}</h2>
                    <div class="verify-tip">
                        <i class="el-icon-info"></i>
                        <span>{{ $t('account_redesign.up_email_no_pending') }}</span>
                    </div>
                    <el-button class="back-btn" type="primary" @click="goToProfile">
                        {{ $t('account_redesign.up_email_back_to_profile') }}
                    </el-button>
                </template>
            </section>
        </div>
    </div>
</template>

<script>
import { mineEmailChange, mineEmailVerify, mineEmailResend } from '@/api/modules/material.js'

export default {
    name: 'EmailVerify',
    data() {
        return {
            // sending=正在发送验证码, verify=已发送可输入验证码, error=发送失败, idle=无待处理
            status: 'idle',
            loading: false,
            resending: false,
            resendCooldown: 0,
            cooldownTimer: null,
            pendingEmail: '',
            pendingPassword: '',
            errorMsg: '',
            form: {
                code: ''
            }
        }
    },
    computed: {
        rules() {
            return {
                code: [
                    { required: true, message: this.$t('account_redesign.up_email_verify_code'), trigger: 'blur' },
                    { min: 4, max: 10, message: this.$t('account_redesign.up_email_verify_code'), trigger: 'blur' }
                ]
            }
        }
    },
    mounted() {
        this.pendingEmail = localStorage.getItem('pendingEmailChange') || ''
        this.pendingPassword = localStorage.getItem('pendingEmailPassword') || ''
        if (this.pendingEmail && this.pendingPassword) {
            this.sendCode()
        } else {
            this.status = 'idle'
        }
    },
    beforeDestroy() {
        if (this.cooldownTimer) {
            clearInterval(this.cooldownTimer)
        }
    },
    methods: {
        async sendCode() {
            this.status = 'sending'
            try {
                const res = await mineEmailChange({ newEmail: this.pendingEmail, password: this.pendingPassword })
                if (res && res.state && res.state.code === 200) {
                    this.status = 'verify'
                    this.startCooldown(60)
                } else {
                    this.status = 'error'
                    this.errorMsg = (res && res.state && res.state.msg) || ''
                    this.clearPending()
                }
            } catch (e) {
                console.error('send email code error:', e)
                this.status = 'error'
                this.errorMsg = (e && e.response && e.response.data && e.response.data.state && e.response.data.state.msg) || ''
                this.clearPending()
            }
        },
        clearPending() {
            // 发送失败时清理密码，保留邮箱用于显示（用户可回 Profile 重新申请）
            localStorage.removeItem('pendingEmailPassword')
        },
        startCooldown(seconds) {
            this.resendCooldown = seconds
            if (this.cooldownTimer) {
                clearInterval(this.cooldownTimer)
            }
            this.cooldownTimer = setInterval(() => {
                this.resendCooldown--
                if (this.resendCooldown <= 0) {
                    this.resendCooldown = 0
                    clearInterval(this.cooldownTimer)
                    this.cooldownTimer = null
                }
            }, 1000)
        },
        async handleSubmit() {
            try {
                await this.$refs.form.validate()
            } catch (e) {
                return
            }
            this.loading = true
            try {
                const res = await mineEmailVerify({ code: this.form.code })
                if (res && res.state && res.state.code === 200) {
                    this.$message.success(this.$t('account_redesign.up_email_verify_success'))
                    localStorage.removeItem('token')
                    localStorage.removeItem('userId')
                    localStorage.removeItem('customerId')
                    localStorage.removeItem('pendingEmailChange')
                    localStorage.removeItem('pendingEmailPassword')
                    setTimeout(() => {
                        this.$router.replace('/login')
                    }, 1500)
                } else {
                    this.$message.error(this.$t('account_redesign.up_email_verify_fail'))
                }
            } catch (e) {
                console.error('verify email error:', e)
                this.$message.error(this.$t('account_redesign.up_email_verify_fail'))
            } finally {
                this.loading = false
            }
        },
        async handleResend() {
            if (this.resendCooldown > 0) return
            this.resending = true
            try {
                const res = await mineEmailResend({})
                if (res && res.state && res.state.code === 200) {
                    this.$message.success(this.$t('account_redesign.up_email_code_sent'))
                    this.startCooldown(60)
                } else {
                    const code = res && res.state && res.state.code
                    if (code === 406) {
                        this.startCooldown(60)
                    } else {
                        this.$message.error(this.$t('account_redesign.up_email_verify_fail'))
                    }
                }
            } catch (e) {
                console.error('resend email code error:', e)
                this.$message.error(this.$t('account_redesign.up_email_verify_fail'))
            } finally {
                this.resending = false
            }
        },
        goToProfile() {
            localStorage.removeItem('pendingEmailChange')
            localStorage.removeItem('pendingEmailPassword')
            // 返回上一页（User Profile），如果没有历史记录则跳转到首页
            if (window.history.length > 1) {
                this.$router.back()
            } else {
                this.$router.push('/')
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.boxlogo {
    position: relative;
    width: 100%;
    height: 100vh;
    min-height: 600px;
    overflow: hidden;
    background: url('~@/assets/bg/bg-1.jpg') no-repeat center center;
    background-size: cover;

    .brand {
        position: absolute;
        top: 30px;
        left: 60px;
        z-index: 10;
        display: flex;
        align-items: center;
        gap: 18px;

        .logo {
            height: 72px;
            display: block;
        }

        .brand-divider {
            width: 1px;
            height: 48px;
            background: linear-gradient(to bottom, transparent 0%, rgba(48, 78, 114, 0.45) 50%, transparent 100%);
            flex-shrink: 0;
        }

        .brand-text {
            display: flex;
            flex-direction: column;
            justify-content: center;
            line-height: 1.1;
        }

        .brand-title {
            margin: 0;
            font-family: 'Source Han Sans CN', 'PingFang SC', 'Microsoft YaHei', sans-serif;
            font-size: 26px;
            font-weight: 700;
            letter-spacing: 0.5px;
            color: #1f2937;
            background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-hover) 60%, var(--primary-color) 100%);
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
        }

        .brand-subtitle {
            margin: 6px 0 0 0;
            font-family: 'Helvetica Neue', 'Arial', sans-serif;
            font-size: 11px;
            font-weight: 500;
            letter-spacing: 2.5px;
            color: #6b7280;
            text-transform: uppercase;
        }
    }

    .login_in_wrap {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        z-index: 5;
    }

    .login_in {
        position: relative;
        width: 380px;
        background: #ffffff;
        filter: drop-shadow(0 8px 24px rgba(48, 78, 114, 0.18)) drop-shadow(0 2px 6px rgba(0, 0, 0, 0.04));
        clip-path: polygon(
            3.13% 0%, 96.87% 0%,
            100% 2.68%, 100% 44.36%,
            97.91% 45.26%, 97.91% 54.56%,
            100% 55.46%, 100% 97.32%,
            96.87% 100%, 3.13% 100%,
            0% 97.32%, 0% 55.46%,
            2.09% 54.56%, 2.09% 45.26%,
            0% 44.36%, 0% 2.68%
        );
        padding: 32px 32px 24px 32px;

        .loginTitle {
            text-align: center;
            font-family: 'Source Han Sans CN';
            font-size: 20px;
            font-weight: 600;
            color: #1f2937;
            margin: 0 0 16px 0;
            letter-spacing: 1px;
        }

        .verify-tip {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            background: #ecf5ff;
            border: 1px solid #d9ecff;
            border-radius: 6px;
            padding: 10px 12px;
            margin-bottom: 20px;

            i {
                color: #409eff;
                font-size: 16px;
                margin-top: 2px;
                flex-shrink: 0;
            }

            span {
                color: #3375b9;
                font-size: 12px;
                line-height: 1.5;
                word-break: break-all;
            }

            &.error-tip {
                background: #fef0f0;
                border-color: #fde2e2;

                i {
                    color: #f56c6c;
                }

                span {
                    color: #c45656;
                }
            }
        }

        .back-btn {
            width: 100%;
            height: 46px;
            border-radius: 8px;
            background-color: var(--primary-color) !important;
            border-color: var(--primary-color) !important;
            color: #ffffff;
            font-size: 15px;
            font-weight: 500;
            letter-spacing: 1px;

            &:hover {
                background-color: var(--primary-color-hover) !important;
                border-color: var(--primary-color-hover) !important;
            }
        }

        .login_form {
            .el-form-item {
                margin-bottom: 18px;
            }

            .resend-item {
                margin-top: -8px !important;
                margin-bottom: 0 !important;
                text-align: center;

                ::v-deep .el-form-item__content {
                    line-height: 1.8 !important;
                    display: flex;
                    justify-content: center;
                }
            }

            .el-input {
                ::v-deep .el-input__inner {
                    height: 46px;
                    line-height: 46px;
                    border: 1px solid #e8ecf1;
                    border-radius: 8px;
                    background-color: #f5f7fa;
                    font-size: 14px;
                    color: #1f2937;
                    padding-left: 44px;
                    transition: all 0.2s ease;
                    letter-spacing: 2px;

                    &::placeholder {
                        color: #9ca3af;
                        letter-spacing: 0;
                    }

                    &:focus {
                        border-color: var(--primary-color);
                        background-color: #ffffff;
                        box-shadow: 0 0 0 3px rgba(48, 78, 114, 0.12);
                    }

                    &:hover {
                        border-color: #9cb4d8;
                    }
                }

                ::v-deep .el-input__prefix {
                    left: 0;
                    width: 40px;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                ::v-deep .el-input__prefix-inner {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                }

                ::v-deep .el-icon-key {
                    font-size: 16px;
                    color: #6b7280;
                }
            }

            ::v-deep .el-button {
                width: 100%;
                height: 48px;
                margin-top: 8px;
                background-color: var(--primary-color) !important;
                border-color: var(--primary-color) !important;
                border-radius: 8px;
                font-size: 15px;
                font-weight: 500;
                letter-spacing: 2px;
                color: #ffffff;
                transition: all 0.2s ease;

                &:hover {
                    background-color: var(--primary-color-hover) !important;
                    border-color: var(--primary-color-hover) !important;
                    box-shadow: 0 4px 12px rgba(48, 78, 114, 0.3);
                }

                &:active {
                    background-color: var(--primary-color) !important;
                    border-color: var(--primary-color) !important;
                }

                &.is-loading {
                    background-color: var(--primary-color) !important;
                }
            }

            .resend-item ::v-deep .el-button--text {
                width: auto;
                height: auto;
                margin-top: 0;
                background-color: transparent !important;
                border: none;
                color: var(--primary-color);
                font-size: 13px;
                letter-spacing: 0;
                box-shadow: none;

                &:hover {
                    color: var(--primary-color-hover);
                    background-color: transparent !important;
                }

                &.is-disabled {
                    color: #c0c4cc;
                }
            }
        }
    }
}

@media only screen and (max-width: 1024px) {
    .boxlogo {
        .login_in_wrap {
            left: 50%;
            transform: translate(-50%, -50%);
        }

        .brand {
            left: 20px;
            top: 18px;
            gap: 10px;

            .logo {
                height: 40px;
            }

            .brand-divider {
                height: 28px;
            }

            .brand-title {
                font-size: 16px;
            }

            .brand-subtitle {
                font-size: 9px;
                letter-spacing: 1.5px;
            }
        }
    }
}
</style>
